import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, BriefcaseIcon, TrendingUpIcon, AwardIcon } from 'lucide-react';
interface TimelinePhase {
  id: string;
  phase: string;
  period: string;
  title: string;
  icon: React.ElementType;
  summary: string;
  highlights: string[];
  roles: {
    title: string;
    org: string;
    period: string;
  }[];
}
const phases: TimelinePhase[] = [{
  id: 'phase-1',
  phase: 'Phase I',
  period: 'Pre-2013',
  title: 'Foundation & Enterprise Formation',
  icon: BriefcaseIcon,
  summary: 'Established core competencies in brand identity, visual systems, and enterprise communication design. Built foundational expertise in design operations, stakeholder management, and cross-functional collaboration within complex organizational environments.',
  highlights: ['Developed foundational expertise in brand identity and visual communication systems', 'Led first enterprise-scale design projects spanning multiple business units', 'Established early design operations frameworks for distributed creative teams', 'Built stakeholder management capabilities across executive and operational levels', 'Cultivated deep expertise in print, digital, and environmental brand applications'],
  roles: [{
    title: 'Senior Designer',
    org: 'Enterprise Communications',
    period: '2010–2013'
  }, {
    title: 'Brand Designer',
    org: 'Creative Agency',
    period: '2007–2010'
  }, {
    title: 'Junior Designer',
    org: 'Design Studio',
    period: '2005–2007'
  }]
}, {
  id: 'phase-2',
  phase: 'Phase II',
  period: '2013–2018',
  title: 'Brand & Experience Leadership',
  icon: TrendingUpIcon,
  summary: 'Ascended to senior leadership roles directing brand strategy, customer experience design, and multi-channel marketing systems for regional and national organizations. Developed expertise in omni-channel experience design, team leadership, and brand governance.',
  highlights: ['Directed brand strategy and creative direction for multi-division organizations', 'Led customer experience transformation initiatives across digital and physical channels', 'Built and managed design teams of 8–15 professionals across multiple disciplines', 'Established design governance frameworks adopted across enterprise divisions', 'Delivered measurable business outcomes through experience-led brand programs'],
  roles: [{
    title: 'Director of Brand Experience',
    org: 'National Retail Organization',
    period: '2016–2018'
  }, {
    title: 'Senior Brand Strategist',
    org: 'Enterprise Brand Consultancy',
    period: '2013–2016'
  }]
}, {
  id: 'phase-3',
  phase: 'Phase III',
  period: '2018–Present',
  title: 'Enterprise & Federal Modernization',
  icon: AwardIcon,
  summary: 'Elevated to Senior Director-level leadership driving enterprise-scale digital modernization, federal accessibility compliance, AI integration strategy, and omni-channel brand ecosystem architecture. Operating at the intersection of technology, governance, and human-centered design.',
  highlights: ['Led digital modernization of 15+ White House and federal agency web properties', 'Directed global retail CX transformation achieving 40% bounce reduction and 15% revenue growth', 'Architected multi-division brand re-architecture unifying 7 brand identities', 'Established AI/GAI integration frameworks for enterprise design workflows', 'Built and led cross-functional teams of 20+ across design, technology, and strategy'],
  roles: [{
    title: 'Senior Director, Product & Brand Systems',
    org: 'Enterprise / Federal',
    period: '2021–Present'
  }, {
    title: 'Director, Digital Experience',
    org: 'Ashley Global Retail',
    period: '2018–2021'
  }]
}];
export function CareerTimeline() {
  const [expandedId, setExpandedId] = useState<string | null>('phase-3');
  const handleToggle = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };
  return <section id="timeline" className="py-24 lg:py-32 bg-[#1C1F23]" aria-labelledby="timeline-heading" data-id="element-97">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-98">
        {/* Header */}
        <div className="mb-16" data-id="element-99">
          <span className="inline-flex items-center gap-2 text-[#B08D57] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-100">
            <span className="w-6 h-px bg-[#B08D57]" aria-hidden="true" data-id="element-101" />
            Career Evolution
          </span>
          <h2 id="timeline-heading" className="font-heading font-bold text-[#F7F7F5] text-3xl lg:text-4xl mb-4" data-id="element-102">
            Three Phases of Leadership
          </h2>
          <p className="font-body text-[#9AABB6] max-w-2xl" data-id="element-103">
            A deliberate progression from craft to systems to enterprise
            leadership — each phase building the capabilities that define the
            next.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative" role="list" aria-label="Career timeline phases" data-id="element-104">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-[#B08D57] via-[#B08D57]/50 to-transparent hidden md:block" aria-hidden="true" data-id="element-105" />

          <div className="space-y-6" data-id="element-106">
            {phases.map((phase, index) => {
            const Icon = phase.icon;
            const isExpanded = expandedId === phase.id;
            return <motion.div key={phase.id} className="relative" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} role="listitem" data-id="element-107">
                  {/* Timeline dot */}
                  <div className="absolute left-4 top-6 w-4 h-4 rounded-full border-2 border-[#B08D57] bg-[#1C1F23] hidden md:block z-10" aria-hidden="true" data-id="element-108">
                    {isExpanded && <motion.div className="absolute inset-1 rounded-full bg-[#B08D57]" initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 400
                }} data-id="element-109" />}
                  </div>

                  {/* Phase card */}
                  <div className="md:ml-16" data-id="element-110">
                    <button onClick={() => handleToggle(phase.id)} className="w-full text-left bg-[#2F3A45]/40 hover:bg-[#2F3A45]/60 border border-[#F7F7F5]/10 rounded-xl p-6 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57]" aria-expanded={isExpanded} aria-controls={`timeline-content-${phase.id}`} data-id="element-111">
                      <div className="flex items-center justify-between gap-4" data-id="element-112">
                        <div className="flex items-center gap-4" data-id="element-113">
                          <div className="w-10 h-10 rounded-lg bg-[#B08D57]/20 flex items-center justify-center flex-shrink-0" aria-hidden="true" data-id="element-114">
                            <Icon className="w-5 h-5 text-[#B08D57]" data-id="element-115" />
                          </div>
                          <div data-id="element-116">
                            <div className="flex items-center gap-3 mb-1" data-id="element-117">
                              <Badge variant="gold" className="text-xs" data-id="element-118">
                                {phase.phase}
                              </Badge>
                              <span className="font-body text-[#B08D57] text-xs tracking-widest" data-id="element-119">
                                {phase.period}
                              </span>
                            </div>
                            <h3 className="font-heading font-semibold text-[#F7F7F5] text-lg" data-id="element-120">
                              {phase.title}
                            </h3>
                          </div>
                        </div>
                        <motion.div animate={{
                      rotate: isExpanded ? 180 : 0
                    }} transition={{
                      duration: 0.25
                    }} aria-hidden="true" data-id="element-121">
                          <ChevronDownIcon className="w-5 h-5 text-[#B08D57]" data-id="element-122" />
                        </motion.div>
                      </div>
                    </button>

                    {/* Expanded content */}
                    <AnimatePresence data-id="element-123">
                      {isExpanded && <motion.div id={`timeline-content-${phase.id}`} initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.35,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-124">
                          <div className="bg-[#2F3A45]/20 border border-[#F7F7F5]/10 border-t-0 rounded-b-xl p-6 space-y-6" data-id="element-125">
                            {/* Summary */}
                            <p className="font-body font-light text-[#ADBEC8] leading-relaxed" data-id="element-126">
                              {phase.summary}
                            </p>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-127">
                              {/* Highlights */}
                              <div data-id="element-128">
                                <h4 className="font-heading font-semibold text-[#F7F7F5] text-sm mb-3 uppercase tracking-widest" data-id="element-129">
                                  Key Highlights
                                </h4>
                                <ul className="space-y-2" role="list" data-id="element-130">
                                  {phase.highlights.map((h, i) => <li key={i} className="flex items-start gap-2 font-body text-[#ADBEC8] text-sm" data-id="element-131">
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#B08D57] mt-1.5 flex-shrink-0" aria-hidden="true" data-id="element-132" />
                                      {h}
                                    </li>)}
                                </ul>
                              </div>

                              {/* Roles */}
                              <div data-id="element-133">
                                <h4 className="font-heading font-semibold text-[#F7F7F5] text-sm mb-3 uppercase tracking-widest" data-id="element-134">
                                  Roles
                                </h4>
                                <div className="space-y-3" data-id="element-135">
                                  {phase.roles.map((role, i) => <div key={i} className="border-l-2 border-[#B08D57]/40 pl-3" data-id="element-136">
                                      <div className="font-body font-semibold text-[#F7F7F5] text-sm" data-id="element-137">
                                        {role.title}
                                      </div>
                                      <div className="font-body text-[#9AABB6] text-xs" data-id="element-138">
                                        {role.org} · {role.period}
                                      </div>
                                    </div>)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}
// Need Badge inline since it's used in CareerTimeline
function Badge({
  children,
  variant,
  className = ''
}: {
  children: React.ReactNode;
  variant: string;
  className?: string;
}) {
  const variantClasses: Record<string, string> = {
    gold: 'bg-[#B08D57]/15 text-[#8E6F3E] border border-[#B08D57]/30'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold font-body ${variantClasses[variant] || ''} ${className}`} data-id="element-139">
      {children}
    </span>;
}